import { useEffect, useState, useContext, useReducer, createContext } from 'react';
import useWindowSize from '@/hooks/utils/useWindowSize';
import { arrayMove } from '@dnd-kit/sortable';
import uniqid from 'uniqid';
import { dequal } from 'dequal';

// Hooks
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';

// Lib
import { LAST_30_DAYS } from '@/lib/periods';
import { CUSTOM, PREVIOUS_PERIOD } from '@/lib/periods';

const PANEL_AVAILABILITY_BREAK_POINT = 1024;

const DashboardContext = createContext();

export const EDIT_TITLE = 'EDIT_TITLE';
export const SET_PERIOD = 'SET_PERIOD';
export const TOGGLE_WIDGETS_PANEL = 'TOGGLE_WIDGETS_PANEL';
export const CHANGE_PANEL_AVAILABILTY = 'CHANGE_PANEL_AVAILABILTY';
export const SELECT_SOCIAL_PROFILE = 'SELECT_SOCIAL_PROFILE';
export const SELECT_GROUP = 'SELECT_GROUP';
export const ADD_ONE_WIDGET = 'ADD_ONE_WIDGET';
export const ADD_ONE_WIDGET_LASTLY = 'ADD_ONE_WIDGET_LASTLY';
export const ADD_ONE_WIDGET_IN_SECTION = 'ADD_ONE_WIDGET_IN_SECTION';
export const MOVE_ONE_WIDGET = 'MOVE_ONE_WIDGET';
export const REMOVE_ONE_WIDGET = 'REMOVE_ONE_WIDGET';
export const REMOVE_ALL_WIDGETS = 'REMOVE_ALL_WIDGETS';
export const REPLACE_SOCIAL_PROFILE = 'REPLACE_SOCIAL_PROFILE';
export const REPLACE_GROUP = 'REPLACE_GROUP';
export const EDIT_ONE_WIDGET_DATA = 'EDIT_ONE_WIDGET_DATA';
export const EDIT_ONE_WIDGET_PARAMETERS = 'EDIT_ONE_WIDGET_PARAMETERS';
export const SAVE_ALL_CHANGES = 'SAVE_ALL_CHANGES';
export const CANCEL_ALL_CHANGES = 'CANCEL_ALL_CHANGES';
export const SET_ERROR = 'SET_ERROR';
export const RESET_ERROR = 'RESET_ERROR';

export const transformWidgetUniqIdToBaseId = (id) => id.split('-')[0];

const setUniqIdToWidget = (widget) => ({
    ...widget,
    id: uniqid(`${widget.id}-`)
});

const setBaseIdToWidget = (widget) => ({
    ...widget,
    id: transformWidgetUniqIdToBaseId(widget.id)
});

const formatDashboardData = (data) => {
    const startDate = data?.start?.slice(0, 19);
    const endDate = data?.end?.slice(0, 19);
    return {
        ...data,
        period: {
            period: data?.period ?? LAST_30_DAYS,
            date: [startDate, endDate],
            start: startDate,
            end: endDate,
            compareTo: data?.compareTo ?? PREVIOUS_PERIOD
        },
        widgets: data?.widgets?.map((widget) =>
            widget.widgets?.length
                ? {
                      ...setUniqIdToWidget(widget),
                      widgets: widget.widgets.map((item) => setUniqIdToWidget(item))
                  }
                : setUniqIdToWidget(widget)
        )
    };
};

const getUsedSPandGroupsIds = (widgets) => {
    const usedSPandGroupsIds = {
        social_profiles: [],
        groups: []
    };

    const addSPIds = (widgets) => {
        widgets.forEach((widget) => {
            widget.social_profiles?.forEach((socialProfile) => {
                if (!usedSPandGroupsIds.social_profiles.includes(socialProfile)) {
                    usedSPandGroupsIds.social_profiles.push(socialProfile);
                }
            });
            widget.groups?.forEach((group) => {
                if (!usedSPandGroupsIds.groups.includes(group)) {
                    usedSPandGroupsIds.groups.push(group);
                }
            });
            if (widget.widgets?.length) {
                addSPIds(widget.widgets);
            }
        });
    };

    addSPIds(widgets);

    return usedSPandGroupsIds;
};

const dashboardReducer = (state, action) => {
    switch (action.type) {
        case EDIT_TITLE:
            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    name: action?.payload
                }
            };
        case SET_PERIOD: {
            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        period: {
                            ...state?.dashboard?.data?.period,
                            ...action?.payload
                        }
                    }
                }
            };
        }
        case TOGGLE_WIDGETS_PANEL: {
            return {
                ...state,
                interactions: {
                    ...state?.interactions,
                    isPanelOpen: !state?.interactions?.isPanelOpen
                }
            };
        }
        case CHANGE_PANEL_AVAILABILTY: {
            return {
                ...state,
                interactions: {
                    ...state?.interactions,
                    isPanelAllowed: action?.payload
                }
            };
        }
        case SELECT_SOCIAL_PROFILE: {
            return {
                ...state,
                interactions: {
                    ...state?.interactions,
                    selectedSocialProfile: action?.payload
                }
            };
        }
        case SELECT_GROUP: {
            return {
                ...state,
                interactions: {
                    ...state?.interactions,
                    selectedGroup: action?.payload
                }
            };
        }
        case ADD_ONE_WIDGET: {
            const addedWidget = setUniqIdToWidget(action?.payload?.widget);

            const widgets = [
                ...(state?.dashboard?.data?.widgets ?? []).slice(
                    0,
                    action?.payload?.destinationIndex
                ),
                addedWidget,
                ...(state?.dashboard?.data?.widgets ?? []).slice(
                    action?.payload?.destinationIndex,
                    state?.dashboard?.data?.widgets?.length
                )
            ];

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        ...getUsedSPandGroupsIds(widgets),
                        widgets
                    }
                },
                interactions: {
                    ...state?.interactions,
                    idDroppedWidget: addedWidget.id
                }
            };
        }
        case ADD_ONE_WIDGET_LASTLY: {
            const addedWidget = setUniqIdToWidget(action?.payload?.widget);
            const widgets = [...(state?.dashboard?.data?.widgets ?? []), addedWidget];

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        ...getUsedSPandGroupsIds(widgets),
                        widgets
                    }
                },
                interactions: {
                    ...state?.interactions,
                    idDroppedWidget: addedWidget.id
                }
            };
        }
        case ADD_ONE_WIDGET_IN_SECTION: {
            const addedWidget = setUniqIdToWidget(action?.payload?.widget);

            const section = state?.dashboard?.data?.widgets?.find(
                (widget) => widget.id === action?.payload?.sectionId
            );

            if (section?.widgets) {
                section['widgets'] = [
                    ...(section.widgets ?? []).slice(0, action?.payload?.destinationIndex),
                    addedWidget,
                    ...(section.widgets ?? []).slice(
                        action?.payload?.destinationIndex,
                        section.widgets.length
                    )
                ];
            } else {
                section['widgets'] = [addedWidget];
            }

            const widgets = state?.dashboard?.data?.widgets?.map((widget) =>
                widget.id === action?.payload?.sectionId ? section : widget
            );

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        ...getUsedSPandGroupsIds(widgets),
                        widgets
                    }
                },
                interactions: {
                    ...state?.interactions,
                    idDroppedWidget: addedWidget.id
                }
            };
        }
        case MOVE_ONE_WIDGET: {
            let widgets = [];

            // When widgets moves at the root
            if (
                action?.payload?.from?.sectionId === null &&
                action?.payload?.to?.sectionId === null
            ) {
                widgets = arrayMove(
                    state?.dashboard?.data?.widgets,
                    action?.payload?.from?.index,
                    action?.payload?.to?.index
                );
                // When widget moves inside a section
            } else if (action?.payload?.from?.sectionId === action?.payload?.to?.sectionId) {
                widgets = state?.dashboard?.data?.widgets?.map((widget) =>
                    widget.id === action?.payload?.from?.sectionId
                        ? {
                              ...widget,
                              widgets: arrayMove(
                                  widget.widgets,
                                  action?.payload?.from?.index,
                                  action?.payload?.to?.index
                              )
                          }
                        : widget
                );
                // When widget moves across sections / root
            } else {
                if (action?.payload?.from?.sectionId === null) {
                    widgets = state?.dashboard?.data?.widgets.filter(
                        (widget, index) => index !== action?.payload?.from?.index
                    );
                } else {
                    widgets = state?.dashboard?.data?.widgets?.map((widget) =>
                        widget.id === action?.payload?.from?.sectionId
                            ? {
                                  ...widget,
                                  widgets: widget.widgets.filter(
                                      (item, index) => index !== action?.payload?.from?.index
                                  )
                              }
                            : widget
                    );
                }

                if (action?.payload?.to?.sectionId === null) {
                    widgets = [
                        ...(widgets ?? []).slice(0, action?.payload?.to?.index),
                        action?.payload?.widget,
                        ...(widgets ?? []).slice(action?.payload?.to?.index, widgets?.length)
                    ];
                } else {
                    widgets = widgets.map((widget) =>
                        widget.id === action?.payload?.to?.sectionId
                            ? {
                                  ...widget,
                                  widgets: [
                                      ...(widget.widgets ?? []).slice(
                                          0,
                                          action?.payload?.to?.index
                                      ),
                                      action?.payload?.widget,
                                      ...(widget.widgets ?? []).slice(
                                          action?.payload?.to?.index,
                                          widget.widgets?.length
                                      )
                                  ]
                              }
                            : widget
                    );
                }
            }

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        widgets
                    }
                }
            };
        }
        case REMOVE_ONE_WIDGET: {
            const widgets = state?.dashboard?.data?.widgets?.find(
                (widget) => widget.id === action?.payload?.id
            )
                ? state?.dashboard?.data?.widgets?.filter((item) => item.id !== action?.payload?.id)
                : state?.dashboard?.data?.widgets.map((widget) =>
                      widget.widgets?.find((item) => item.id === action?.payload?.id)
                          ? {
                                ...widget,
                                widgets: widget.widgets?.filter(
                                    (item) => item.id !== action?.payload?.id
                                )
                            }
                          : widget
                  );

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard.data,
                        ...getUsedSPandGroupsIds(widgets),
                        widgets
                    }
                }
            };
        }
        case REMOVE_ALL_WIDGETS: {
            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        social_profiles: [],
                        groups: [],
                        widgets: []
                    }
                }
            };
        }
        case REPLACE_SOCIAL_PROFILE: {
            const widgets = state?.dashboard?.data?.widgets?.map((widget) =>
                widget.id !== action?.payload?.id
                    ? widget.widgets?.length
                        ? {
                              ...widget,
                              widgets: widget.widgets.map((item) =>
                                  item.id !== action?.payload?.id
                                      ? item
                                      : { ...item, social_profiles: [action?.payload?.spId] }
                              )
                          }
                        : widget
                    : { ...widget, social_profiles: [action?.payload?.spId] }
            );

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        ...getUsedSPandGroupsIds(widgets),
                        widgets
                    }
                }
            };
        }
        case REPLACE_GROUP: {
            const widgets = state?.dashboard?.data?.widgets?.map((widget) =>
                widget.id !== action?.payload?.id
                    ? widget.widgets?.length
                        ? {
                              ...widget,
                              widgets: widget.widgets.map((item) =>
                                  item.id !== action?.payload?.id
                                      ? item
                                      : { ...item, groups: [action?.payload?.groupId] }
                              )
                          }
                        : widget
                    : { ...widget, groups: [action?.payload?.groupId] }
            );

            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        ...getUsedSPandGroupsIds(widgets),
                        widgets
                    }
                }
            };
        }
        case EDIT_ONE_WIDGET_DATA: {
            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        widgets: state?.dashboard?.data?.widgets?.map((widget) =>
                            widget.id !== action?.payload?.id
                                ? widget.widgets?.length
                                    ? {
                                          ...widget,
                                          widgets: widget.widgets.map((item) =>
                                              item.id === action?.payload?.id
                                                  ? { ...item, data: action?.payload?.data }
                                                  : item
                                          )
                                      }
                                    : widget
                                : { ...widget, data: action?.payload?.data }
                        )
                    }
                }
            };
        }
        case EDIT_ONE_WIDGET_PARAMETERS: {
            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: {
                        ...state?.dashboard?.data,
                        widgets: state?.dashboard?.data?.widgets?.map((widget) =>
                            widget.id !== action?.payload?.id
                                ? widget.widgets?.length
                                    ? {
                                          ...widget,
                                          widgets: widget.widgets.map((item) =>
                                              item.id === action?.payload?.id
                                                  ? {
                                                        ...item,
                                                        parameters: action?.payload?.parameters
                                                    }
                                                  : item
                                          )
                                      }
                                    : widget
                                : { ...widget, parameters: action?.payload?.parameters }
                        )
                    }
                }
            };
        }
        case SAVE_ALL_CHANGES: {
            return {
                ...state,
                initialDashboard: {
                    ...state?.dashboard,
                    updated_at: action?.payload?.updatedData?.updated_at,
                    data: {
                        ...state?.initialDashboard?.data,
                        ...state?.dashboard?.data?.period,
                        social_profiles: state?.dashboard?.data?.social_profiles,
                        groups: state?.dashboard?.data?.groups,
                        widgets: state?.dashboard?.data?.widgets?.map((widget) =>
                            widget.widgets?.length
                                ? {
                                      ...setBaseIdToWidget(widget),
                                      widgets: widget.widgets.map((item) => setBaseIdToWidget(item))
                                  }
                                : setBaseIdToWidget(widget)
                        )
                    }
                }
            };
        }
        case CANCEL_ALL_CHANGES: {
            return {
                ...state,
                dashboard: {
                    ...state?.dashboard,
                    data: formatDashboardData(state?.initialDashboard?.data)
                }
            };
        }
        case SET_ERROR: {
            return {
                ...state,
                error: action?.payload
            };
        }
        case RESET_ERROR: {
            return {
                ...state,
                error: null
            };
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
};

const DashboardProvider = ({ value, children }) => {
    const { width: windowWidth } = useWindowSize();

    const { activeSocialProfile } = useActiveSocialProfile();

    const { activeGroup } = useActiveGroup();

    const { activatedSpGroups } = useSPGroups({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const [areAnyChanges, setAreAnyChanges] = useState(false);

    const initialData = {
        initialDashboard: value,
        dashboard: {
            ...value,
            data: formatDashboardData(value?.data)
        },
        interactions: {
            isPanelOpen: true,
            isPanelAllowed: windowWidth >= 1024,
            selectedSocialProfile:
                activeSocialProfile ?? activeGroup?.social_profiles?.filter((sp) => sp.active)[0],
            selectedGroup: activeGroup ?? activatedSpGroups?.[0] ?? null,
            idDroppedWidget: null
        },
        error: null
    };

    const [data, dispatch] = useReducer(dashboardReducer, initialData);

    const hasDashboardChanged = () =>
        !dequal(
            {
                widgets: data?.initialDashboard?.data?.widgets.map((widget) => ({
                    social_profiles: widget.social_profiles,
                    groups: widget.groups,
                    id: widget.id,
                    data: widget.data ?? {},
                    parameters: widget.parameters ?? {},
                    widgets: widget.widgets?.map((item) => ({
                        social_profiles: item.social_profiles,
                        groups: item.groups,
                        id: item.id,
                        data: item.data ?? {},
                        parameters: item.parameters ?? {}
                    }))
                })),
                period:
                    data?.initialDashboard?.data?.period === CUSTOM
                        ? {
                              start: data?.initialDashboard?.data?.start?.slice(0, 19),
                              end: data?.initialDashboard?.data?.end?.slice(0, 19)
                          }
                        : data?.initialDashboard?.data?.period
            },
            {
                widgets: data?.dashboard.data.widgets.map((widget) => ({
                    social_profiles: widget.social_profiles,
                    groups: widget.groups,
                    id: transformWidgetUniqIdToBaseId(widget.id),
                    data: widget.data ?? {},
                    parameters: widget.parameters ?? {},
                    widgets: widget.widgets?.map((item) => ({
                        social_profiles: item.social_profiles,
                        groups: item.groups,
                        id: transformWidgetUniqIdToBaseId(item.id),
                        data: item.data ?? {},
                        parameters: item.parameters ?? {}
                    }))
                })),
                period:
                    data?.dashboard?.data?.period?.period === CUSTOM
                        ? {
                              start: data?.dashboard?.data?.period?.start,
                              end: data?.dashboard?.data?.period?.end
                          }
                        : data?.dashboard?.data?.period?.period
            }
        );

    useEffect(() => {
        if (hasDashboardChanged() && !areAnyChanges) {
            setAreAnyChanges(true);
        }
        if (!hasDashboardChanged() && areAnyChanges) {
            setAreAnyChanges(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data)]);

    useEffect(() => {
        if (windowWidth >= PANEL_AVAILABILITY_BREAK_POINT && !data?.interactions?.isPanelAllowed) {
            dispatch({
                type: CHANGE_PANEL_AVAILABILTY,
                payload: true
            });
        }
        if (windowWidth < PANEL_AVAILABILITY_BREAK_POINT && data?.interactions?.isPanelAllowed) {
            dispatch({
                type: CHANGE_PANEL_AVAILABILTY,
                payload: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth]);

    return (
        <DashboardContext.Provider
            value={{
                initialDashboard: data.initialDashboard,
                dashboard: data.dashboard,
                interactions: data.interactions,
                error: data.error,
                areAnyChanges,
                hasDashboardChanged,
                dispatch
            }}>
            {children}
        </DashboardContext.Provider>
    );
};

const useDashboardContext = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboardContext should be used within a DashboardProvider');
    }

    return context;
};

export { DashboardProvider, useDashboardContext };
